'use client';

import {
  Content,
  type CollapsibleProps as RadixCollapsibleProps,
  Root,
  Trigger,
} from '@radix-ui/react-collapsible';
import clsx from 'clsx';
import { type ReactNode, useState } from 'react';

import { BaseContainer, type BaseContainerProps } from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';

import Icon from '../Icon';
import styles from './index.module.scss';

/**
 * Type definition for Collapsible component props.
 *
 * Extends the properties of the Radix Collapsible component.
 */

export type CollapsibleProps = Omit<RadixCollapsibleProps, 'title' | 'content'> & {
  /**
   * A React node to render as the title of the collapsible.
   */
  title: ReactNode;

  /**
   * A React node to render as the content of the collapsible.
   */
  children: ReactNode;

  /**
   * The gap above and below the title, as well as the content.
   */
  gap?: BaseContainerProps['padding'];

  /**
   * Whether to show a border at the top of the collapsible.
   *
   * If set, the border color changes when hovered and opened.
   */
  hasBorderTop?: boolean;

  /**
   * A callback that is called when the collapsible is opened or closed.
   */
  onTriggerClick?: () => void;
};

/**
 * Collapsible component (based on Radix Collapsible).
 *
 * Notes:
 * `open` and `onOpenChange` props should only be used when the component is controlled by the parent component.
 *  Otherwise, the component will be uncontrolled and will manage its own state.
 *
 * @see https://www.radix-ui.com/primitives/docs/components/collapsible
 */

export const Collapsible = ({
  title,
  children,
  defaultOpen,
  gap = 'sm',
  hasBorderTop = true,
  onOpenChange,
  onTriggerClick,
  className,
  ...props
}: CollapsibleProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const classes = clsx(
    styles.collapsible,
    hasBorderTop && styles.borderTop,
    open || props.open ? styles.opened : styles.closed,
    className
  );

  const handleChange = (open: boolean) => {
    const setter = onOpenChange || setOpen;
    setter(open);
  };

  return (
    <Root defaultOpen={defaultOpen} onOpenChange={handleChange} className={classes} {...props}>
      <Trigger asChild type={undefined}>
        <Flex
          role="button"
          tabIndex={props.disabled ? -1 : 0}
          aria-disabled={props.disabled || undefined}
          align="center"
          border="bottom"
          justify="space-between"
          paddingTop={gap}
          paddingBottom={gap}
          className={styles.trigger}
          onClick={(event) => {
            if (props.disabled) {
              event.preventDefault();
              return;
            }
            onTriggerClick?.();
          }}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (event.key !== 'Enter' && event.key !== ' ') return;

            event.preventDefault();
            if (!props.disabled && !event.repeat && event.key === 'Enter') {
              event.currentTarget.click();
            }
          }}
          onKeyUp={(event) => {
            if (event.target !== event.currentTarget || event.key !== ' ') return;

            event.preventDefault();
            if (!props.disabled) event.currentTarget.click();
          }}
        >
          {title}
          <Icon type={open ? 'chevronTop' : 'chevronBottom'} />
        </Flex>
      </Trigger>
      <Content asChild>
        <BaseContainer className={styles.content}>
          <BaseContainer paddingTop={gap} paddingBottom={gap}>
            {children}
          </BaseContainer>
        </BaseContainer>
      </Content>
    </Root>
  );
};

export default Collapsible;
